import React, { useState } from "react";

import {
  GetApp as GetAppIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from "@mui/icons-material";
import { Box, IconButton, Theme, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { EditIconButton } from "@/components/IconButtons";
import AllergyIcon from "@/components/Icons/AllergyIcon";
import FoodIntoleranceIcon from "@/components/Icons/FoodIntoleranceIcon";
import PreferredDietIcon from "@/components/Icons/PreferredDietIcon";
import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import { useSnackBar } from "@/hooks/useSnackBar";
import { colors } from "@/theme/overrides/colors";
import { humanize } from "@/utils/string";

import EditDietaryHistoryModal from "./components/EditDietaryHistoryModal";

interface Props {
  clientId: string;
}

const useStyles = makeStyles()((theme: Theme) => ({
  section: {
    padding: 24,
    backgroundColor: colors.white,
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
  iconContainer: {
    background: "#EDF0F5",
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    marginRight: theme.spacing(2),
  },
  downLoadIconContainer: {
    background: colors.white,
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    marginRight: theme.spacing(2),
  },
  icon: {
    width: 20,
    height: 20,
    color: "rgba(125, 135, 151, 1)",
  },
  itemContainer: {
    marginBottom: theme.spacing(2),
  },
  item: {
    padding: theme.spacing(1, 2),
  },
  attachmentContainer: {
    display: "flex",
    flexDirection: "column",
  },
  attachment: {
    background: "#EDF0F5",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  attachmentTitle: {
    flex: 1,
    marginRight: theme.spacing(1),
  },
  flexItem: {
    padding: theme.spacing(2),
    width: "33%",
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },
  title: {
    maxWidth: "100%",
  },
}));

const dietaryHistoryFragment = graphql(`
  fragment DietaryHistorySection on Client {
    id
    dietaryHistory {
      ...ClientDietaryHistory
    }
  }

  fragment ClientDietaryHistory on DietaryHistory {
    id
    clientId
    preferredTypesOfDiet
    allergies2
    foodIntolerance
    files {
      id
      signedId
      filename
      url
    }
    createdAt
    updatedAt
  }
`);

const DietaryHistory: React.FC<Props> = ({ clientId }) => {
  const { classes } = useStyles();
  const [client] = useFragment(dietaryHistoryFragment, `Client:${clientId}`);
  const [modalVisibility, setModalVisibility] = useState<boolean>(false);
  const [SimpleSnackBar, snackBarHelper] = useSnackBar();

  const dietaryHistory = client?.dietaryHistory;
  const preferredTypesOfDiet = dietaryHistory?.preferredTypesOfDiet;
  const allergies = dietaryHistory?.allergies2 ?? [];
  const foodIntolerance = dietaryHistory?.foodIntolerance;
  const files = dietaryHistory?.files ?? [];

  const handleModalClose = () => {
    setModalVisibility(false);
  };

  const handleModalOpen = () => {
    setModalVisibility(true);
  };

  const handleSnackBar = (isSuccessful: boolean) => {
    if (isSuccessful) {
      snackBarHelper.open("Successfully updated");
    } else {
      snackBarHelper.open(
        "There was an error in updating your clients dietary history"
      );
    }
  };

  return (
    <>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={5}
        mb={3}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          Dietary history
        </Typography>
        <Box marginY={3} />
        <EditIconButton onClick={handleModalOpen} color="primary" />
      </Box>
      <section className={classes.section}>
        <Box className={classes.flexItem}>
          <Box display="flex" alignItems="center" marginBottom="12px">
            <div className={classes.iconContainer}>
              <PreferredDietIcon className={classes.icon} />
            </div>
            <Typography variant="body1" noWrap>
              Preferred Types of Diet
            </Typography>
          </Box>
          <div>
            {dietaryHistory?.preferredTypesOfDiet?.map((diet, index) => (
              <Typography
                key={index}
                variant="body2"
                color="textSecondary"
                noWrap
                className={classes.item}
              >
                {diet}
              </Typography>
            )) ?? (
              <Typography variant="body2" color="textSecondary">
                {"N/A"}
              </Typography>
            )}
          </div>
        </Box>

        <Box className={classes.flexItem}>
          <Box display="flex" alignItems="center" marginBottom="12px">
            <div className={classes.iconContainer}>
              <AllergyIcon className={classes.icon} />
            </div>
            <Typography variant="body1" noWrap>
              Allergies
            </Typography>
          </Box>
          <div className={classes.itemContainer}>
            {dietaryHistory?.allergies2?.map((diet, index) => (
              <Typography
                key={index}
                variant="body2"
                color="textSecondary"
                noWrap
                className={classes.item}
              >
                {humanize(diet)}
              </Typography>
            )) ?? (
              <Typography variant="body2" color="textSecondary">
                {"N/A"}
              </Typography>
            )}
          </div>
        </Box>

        <Box className={classes.flexItem}>
          <Box display="flex" alignItems="center" marginBottom="12px">
            <div className={classes.iconContainer}>
              <FoodIntoleranceIcon className={classes.icon} />
            </div>
            <Typography variant="body1" noWrap>
              Food Intolerance
            </Typography>
          </Box>
          {/* Attachments here */}
          <div className={classes.attachmentContainer}>
            {files.map((file) => (
              <div className={classes.attachment} key={file.id}>
                <div className={classes.iconContainer}>
                  <InsertDriveFileIcon className={classes.icon} />
                </div>
                <Typography
                  variant="body1"
                  noWrap
                  className={classes.attachmentTitle}
                >
                  {file.filename}
                </Typography>
                <IconButton
                  href={file.url}
                  target="_blank"
                  download={file.filename}
                  className={classes.downLoadIconContainer}
                  size="large"
                >
                  <GetAppIcon className={classes.icon} />
                </IconButton>
              </div>
            ))}
          </div>
          <Typography variant="body2" color="textSecondary">
            {dietaryHistory?.foodIntolerance ?? "N/A"}
          </Typography>
        </Box>
      </section>
      <EditDietaryHistoryModal
        visible={modalVisibility}
        onClose={handleModalClose}
        showSnackBar={handleSnackBar}
        foodIntolerance={foodIntolerance}
        id={dietaryHistory?.id ?? ""}
        preferredTypesOfDiet={preferredTypesOfDiet}
        allergies={allergies ?? []}
        files={files}
      />
      <SimpleSnackBar />
    </>
  );
};

export default DietaryHistory;
